import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Flame, Wrench, Disc3, FileText, Search, Globe, Monitor, Loader2, FileClock } from "lucide-react";

const TYPE_ICON = { svejsning: Flame, montage: Wrench, slibning: Disc3 };
const TYPE_LABEL = { svejsning: "Svejsning", montage: "Montage", slibning: "Slibning" };

const daDate = (s) => {
  const d = String(s || "").slice(0, 10);
  if (d.length === 10) { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; }
  return "—";
};
const daTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" }); }
  catch (_) { return ""; }
};

export default function DocumentAccessLog() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const h = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (type) params.set("type", type);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      setItems(null);
      api.get(`/document-access-log?${params.toString()}`)
        .then((r) => setItems(r.data.items || []))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(h);
  }, [q, type, dateFrom, dateTo]);

  const anyFilter = q.trim() || type || dateFrom || dateTo;
  const reset = () => { setQ(""); setType(""); setDateFrom(""); setDateTo(""); };

  const grouped = useMemo(() => items || [], [items]);

  return (
    <div className="rise-in" data-testid="doc-log-page">
      <div className="mb-8">
        <p className="label-caps mb-2">Ledelse</p>
        <h1 className="text-4xl sm:text-5xl font-light text-[#1A1919] tracking-tight">Dokument-log</h1>
        <p className="text-stone-500 mt-2 text-sm">Se hvornår produktionsdokumenter er blevet åbnet — søg på varenr. eller dokumentnr. og afgræns på dato.</p>
      </div>

      <div className="pcard p-4 mb-6" data-testid="doc-log-toolbar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <label className="label-caps mb-1.5 block">Søg</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
              <input data-testid="doc-log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Varenr., dokumentnr., titel eller navn…"
                className="w-full h-11 pl-10 pr-3 rounded-xl border border-[#EBE8E3] bg-white text-sm outline-none focus:border-[#A83232]" />
            </div>
          </div>
          <div className="min-w-[160px]">
            <label className="label-caps mb-1.5 block">Afdeling</label>
            <select data-testid="doc-log-type" value={type} onChange={(e) => setType(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-[#EBE8E3] bg-white text-sm outline-none focus:border-[#A83232]">
              <option value="">Alle afdelinger</option>
              <option value="svejsning">Svejsning</option>
              <option value="montage">Montage</option>
              <option value="slibning">Slibning</option>
            </select>
          </div>
          <div className="min-w-[150px]">
            <label className="label-caps mb-1.5 block">Fra dato</label>
            <input data-testid="doc-log-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-[#EBE8E3] bg-white text-sm outline-none focus:border-[#A83232]" />
          </div>
          <div className="min-w-[150px]">
            <label className="label-caps mb-1.5 block">Til dato</label>
            <input data-testid="doc-log-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-[#EBE8E3] bg-white text-sm outline-none focus:border-[#A83232]" />
          </div>
          {anyFilter && (
            <button data-testid="doc-log-reset" onClick={reset} className="h-11 px-4 rounded-xl text-sm font-medium text-stone-600 hover:text-[#1A1919] bg-white border border-[#EBE8E3]">Nulstil</button>
          )}
        </div>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-24 text-stone-400" data-testid="doc-log-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Henter log…</div>
      ) : grouped.length === 0 ? (
        <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center" data-testid="doc-log-empty">
          <div className="h-12 w-12 rounded-2xl bg-[#F3F1EC] flex items-center justify-center mx-auto mb-3"><FileClock className="h-5 w-5 text-stone-400" /></div>
          <p className="text-stone-600 font-medium">Ingen åbninger registreret</p>
          <p className="text-sm text-stone-400 mt-1">{anyFilter ? "Prøv at justere dine filtre." : "Der er endnu ikke logget åbninger af produktionsdokumenter."}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-stone-500 mb-3" data-testid="doc-log-count">{grouped.length} {grouped.length === 1 ? "åbning" : "åbninger"}</p>
          <div className="rounded-2xl border border-[#EBE8E3] bg-white overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_180px_150px_110px_130px] gap-4 px-5 py-3 border-b border-[#F3F1EC] bg-[#FBFAF8]">
              <span className="label-caps">Dokument</span>
              <span className="label-caps">Åbnet af</span>
              <span className="label-caps">Kilde</span>
              <span className="label-caps">Dato</span>
              <span className="label-caps text-right">Tidspunkt</span>
            </div>
            <div className="divide-y divide-[#F3F1EC]">
              {grouped.map((l) => {
                const Icon = TYPE_ICON[l.report_type] || FileText;
                const isPublic = l.source === "public";
                return (
                  <button key={l.id} onClick={() => nav(`/dokument/${l.report_id}`)} data-testid={`doc-log-row-${l.id}`}
                    className="w-full text-left grid grid-cols-1 md:grid-cols-[1fr_180px_150px_110px_130px] gap-2 md:gap-4 px-5 py-4 items-center hover:bg-[#FBFAF8] transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-9 w-9 rounded-lg bg-[#F1DEDA] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#A83232]" /></span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span data-testid={`doc-log-docno-${l.id}`} className="font-mono text-xs font-semibold text-[#A83232] bg-[#F1DEDA] border border-[#E8C9C4] rounded px-1.5 py-0.5 shrink-0">{l.doc_no || "Uden dok.nr."}</span>
                          <p className="font-medium text-[#1A1919] truncate">{l.title || "Uden titel"}</p>
                        </div>
                        <p className="text-xs text-stone-500 truncate mt-0.5">
                          {l.item_no ? <>Varenr. {l.item_no} · </> : null}{TYPE_LABEL[l.report_type] || l.report_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-700 min-w-0">
                      <span className="truncate">{l.user_name}</span>
                    </div>
                    <div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isPublic ? "bg-[#F9F1D9] text-[#8A6E17] border border-[#ECE0B6]" : "bg-[#E7EADF] text-[#7E856C] border border-[#D3D8C6]"}`}>
                        {isPublic ? <Globe className="h-3 w-3" /> : <Monitor className="h-3 w-3" />} {isPublic ? "Offentligt link" : "App"}
                      </span>
                    </div>
                    <div className="text-sm text-stone-700 tabular-nums">{daDate(l.day)}</div>
                    <div className="text-sm text-stone-500 text-left md:text-right tabular-nums">
                      {l.count > 1
                        ? <>{daTime(l.first_opened_at)}–{daTime(l.last_opened_at)} <span className="text-stone-400">· {l.count}×</span></>
                        : daTime(l.first_opened_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
